/**
 * `DEFAULT_CODING_SCREEN_CONFIG` / `DEFAULT_GRADING_SCREEN_CONFIG` — the
 * single hard-coded config the shell renders from today (a backend config source is a separate,
 * later effort). This file imports ONLY types from `./schema` (no runtime value), so it
 * trivially satisfies the project_starburst isolation boundary guard.
 *
 * EMPTY SHELL, WITH TWO INTENTIONAL EXCEPTIONS (CDMX-18402, CDMX-18412): every container is present
 * (each stamping its own `contentKey`, per schema.ts's container-identity objects); every
 * order-checked control list is `[]` EXCEPT `header.patientSummary.fields`, which seeds -- CODING
 * ONLY -- the `PatientInfo` field (`componentId: 'PatientInfo'` at `contentId: 'case.patient'`, the
 * first real slot component the framework renders, CDMX-18402) followed by the `holdAction` field
 * (`componentId: 'HoldAction'` at `contentId: 'session.currentUser'`, the coding bench's Hold
 * trigger, CDMX-18412) -- both registered into `registry.tsx`'s `DEFAULT_SLOT_REGISTRY`.
 * `header.caseContext.fields` stays `[]` in both modes -- it is NOT where either seeded field lives.
 * `DEFAULT_GRADING_SCREEN_CONFIG` overrides `header.patientSummary.fields` back down to JUST
 * `PatientInfo` -- holds are a coder action, so grading never seeds `HoldAction` (see that config's
 * own doc comment below). `header.topBar.visible` is `false` (built but hidden -- still in the DOM
 * for tests, geometry preserved), and `patientSummary`/`caseContext` are `visible: true` (empty
 * containers still render their chrome). This matches `empty-ui.png` plus the deliberate, documented
 * steps from empty shell to first content.
 *
 * `as const satisfies CodingScreenConfig` gives BOTH deeply-readonly literal types AND a structural check, at the exact field
 * shape schema.ts declares, that this object is a valid `CodingScreenConfig` -- catching a
 * shape mistake (wrong/missing container, wrong contentKey literal) at compile time even where
 * `validateConfig` wouldn't notice (it only walks the order-checked list fields, not every
 * container's `contentKey`).
 */

import type { CodingScreenConfig } from './schema';

export const DEFAULT_CODING_SCREEN_CONFIG = {
  mode: 'coding',
  header: {
    topBar: { contentKey: 'topBar', visible: false, items: [] },
    patientSummary: {
      contentKey: 'patientSummary',
      visible: true,
      fields: [
        {
          contentKey: 'patientInfo',
          order: 0,
          visible: true,
          label: 'Patient',
          contentId: 'case.patient',
          componentId: 'PatientInfo'
        },
        {
          contentKey: 'holdAction',
          order: 1,
          visible: true,
          // `label` is REQUIRED by `FieldItem` (extends `Item`), but `HoldAction` uses its own
          // fixed accessible name -- `SlotProps.item` carries no `label` field, so this is a
          // required-but-unused value for this action slot, same as the `PatientInfo` field's
          // `label: 'Patient'` above.
          label: 'Hold',
          // `session.currentUser` is a SHARED namespace (not case.*/workitem.*), so it is legal in
          // BOTH benches per `validateConfig`'s mode<->namespace gate -- even though this field is
          // coding-only (see `DEFAULT_GRADING_SCREEN_CONFIG` below), the contentId itself carries no
          // mode restriction.
          contentId: 'session.currentUser',
          componentId: 'HoldAction',
          presentation: 'plain'
        }
      ]
    },
    caseContext: { contentKey: 'caseContext', visible: true, fields: [] }
  },
  panes: [
    { id: 'left', order: 0, sizePct: 62, minPx: 480 },
    { id: 'right', order: 1, sizePct: 38, minPx: 360 }
  ],
  left: {
    docNav: { contentKey: 'docNav' },
    viewer: { contentKey: 'viewer', contentId: 'doc.body' },
    tabs: []
  },
  right: {
    reviewTabs: { contentKey: 'reviewTabs' },
    reviewContent: { contentKey: 'reviewContent' },
    footerSummary: {
      contentKey: 'footerSummary',
      contentId: 'summary.reviewProgress'
    },
    tabs: [],
    sections: [],
    actions: []
  },
  footer: {
    shortcutStatus: { contentKey: 'shortcutStatus' },
    caseIdentifier: {
      contentKey: 'caseIdentifier',
      contentId: 'case.identifier'
    },
    shortcuts: []
  }
} as const satisfies CodingScreenConfig;

/** The same empty shell as coding, `mode: 'grading'`, with the two data-bound patient/identity
 *  slots renamespaced from `case.*` to `workitem.*` -- a grading bench binds the WORKITEM, not the
 *  case, so the mode<->namespace gate in `validateConfig` rejects a `case.*` contentId under
 *  `mode: 'grading'`. `header.patientSummary`'s PatientInfo field flips to `workitem.patient` and
 *  `footer.caseIdentifier` to `workitem.identifier`; the same `PatientInfo` renderer serves both
 *  (its value is source-agnostic -- the adapter fills it from the CaseRun in coding, the WorkItem in
 *  grading). The other two bound contentIds (`doc.body`, `summary.reviewProgress`) are shared
 *  namespaces, allowed in both benches, so they stay. The same shell serves both; grade-specific
 *  content branches on `mode`, not a fork of this file.
 *
 *  `header.patientSummary.fields` (CDMX-18412) is a FULL REPLACEMENT, not a renamespaced copy of the
 *  coding list: it stays PatientInfo-ONLY, entirely OMITTING the coding default's `holdAction` field
 *  -- placing a hold is a coder action, so a grading bench (reviewing already-coded work) never
 *  renders a Hold trigger. This is a deliberate content difference between the two default configs
 *  (not just a namespace swap), verified by `config/defaultConfig.test.ts`'s GRADING describe block. */
export const DEFAULT_GRADING_SCREEN_CONFIG = {
  ...DEFAULT_CODING_SCREEN_CONFIG,
  mode: 'grading',
  header: {
    ...DEFAULT_CODING_SCREEN_CONFIG.header,
    patientSummary: {
      ...DEFAULT_CODING_SCREEN_CONFIG.header.patientSummary,
      fields: [
        {
          contentKey: 'patientInfo',
          order: 0,
          visible: true,
          label: 'Patient',
          contentId: 'workitem.patient',
          componentId: 'PatientInfo'
        }
      ]
    }
  },
  footer: {
    ...DEFAULT_CODING_SCREEN_CONFIG.footer,
    caseIdentifier: {
      contentKey: 'caseIdentifier',
      contentId: 'workitem.identifier'
    }
  }
} as const satisfies CodingScreenConfig;
